import { useEffect, useRef } from "react";
import { __ } from "services/Translator";
import { GiftIcon, ChevronDownIcon } from "./icons";

// Bonus selector dropdown. Opens upward (CSS), selects an option, and closes on
// outside click. Mirrors the Hub #bonusbox behavior. Keyboard-operable listbox:
// the head is a button (aria-expanded/haspopup), the options are role=option in
// a role=listbox, with arrow/Enter/Space select and Escape close.
export default function BonusSelector({ options, selectedIndex, onSelect, open, setOpen, title, line, note }) {
  const boxRef = useRef(null);
  const headRef = useRef(null);
  const optRefs = useRef([]);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [setOpen]);

  // Move focus onto the active option when the list opens.
  useEffect(() => {
    if (open) {
      const el = optRefs.current[selectedIndex] || optRefs.current[0];
      if (el) el.focus();
    }
  }, [open, selectedIndex]);

  const close = () => {
    setOpen(false);
    if (headRef.current) headRef.current.focus();
  };

  const onHeadKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(!open);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
    }
  };

  const onOptKeyDown = (e, i) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(i);
      close();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const el = optRefs.current[Math.min(i + 1, options.length - 1)];
      if (el) el.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const el = optRefs.current[Math.max(i - 1, 0)];
      if (el) el.focus();
    }
  };

  return (
    <div className={open ? "bonus open" : "bonus"} ref={boxRef}>
      <div
        className="bonus-head"
        ref={headRef}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={__("Choose a bonus")}
        onClick={() => setOpen(!open)}
        onKeyDown={onHeadKeyDown}
      >
        <div className="gift">
          <GiftIcon />
        </div>
        <div className="btxt">
          <b>{title}</b>
          <small>{line}</small>
          {note ? <small className="bpre">{note}</small> : null}
        </div>
        <div className="chev">
          <ChevronDownIcon />
        </div>
      </div>
      <div className="bonus-opts" role="listbox" aria-label={__("Available bonuses")}>
        {options.map((opt, i) => (
          <div
            key={`${opt.id != null ? opt.id : opt.label}-${i}`}
            ref={(el) => (optRefs.current[i] = el)}
            role="option"
            aria-selected={i === selectedIndex}
            tabIndex={open ? 0 : -1}
            className={i === selectedIndex ? "bopt sel" : "bopt"}
            onClick={() => {
              onSelect(i);
              setOpen(false);
            }}
            onKeyDown={(e) => onOptKeyDown(e, i)}
          >
            <div className="bradio" />
            <div className="botxt">
              <b>{opt.label}</b>
              {opt.isPreselected ? <small className="bpre">{__("Your welcome offer")}</small> : null}
              <small>{opt.sub}</small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
